import { Component, OnDestroy, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { MatCardModule } from "@angular/material/card"
import { MatButtonModule } from "@angular/material/button"
import { MatIconModule } from "@angular/material/icon"
import { RouterModule, Router } from "@angular/router"
import { Subscription } from "rxjs"
import { ProfileService, UserProfile, textNotificationsEnabled } from "../../services/profile.service"
import { PermissionService } from "../../services/permission.service"

@Component({
  selector: "app-profile",
  standalone: true,
  imports: [CommonModule, MatCardModule, MatButtonModule, MatIconModule, RouterModule],
  templateUrl: "./profile.component.html",
  styleUrl: "./profile.component.css",
})
export class ProfileComponent implements OnInit, OnDestroy {
  user: UserProfile | null = null
  error = ""
  loading = false
  private profileSub?: Subscription

  constructor(
    private router: Router,
    private profileService: ProfileService,
    private permissionService: PermissionService,
  ) {}

  get showSettingsLink(): boolean {
    return this.permissionService.canAccessUserSettings()
  }

  /** SMS text notifications are advertiser (user) only — hide for staff. */
  get showTextNotifications(): boolean {
    return this.permissionService.getBaseRole() === "user"
  }

  get isStaffRole(): boolean {
    return ["admin", "owner", "station_owner", "station_user"].includes(
      this.permissionService.getBaseRole(),
    )
  }

  get settingsButtonLabel(): string {
    return "Settings"
  }

  get settingsButtonIcon(): string {
    return this.isStaffRole ? "notifications" : "settings"
  }

  get settingsHint(): string {
    return this.isStaffRole
      ? "Manage which events send in-app and email alerts"
      : "Manage text notification preferences"
  }

  ngOnInit(): void {
    this.profileSub = this.profileService.currentUser$.subscribe((u) => {
      if (!u || !this.user) return
      if (this.user.email && u.email && this.user.email !== u.email) return
      this.user = { ...this.user, ...u }
    })
    this.loadUserProfile()
  }

  ngOnDestroy(): void {
    this.profileSub?.unsubscribe()
  }

  /**
   * Username shown on the profile card: the person's name, not their login.
   *
   * `cognito_username` is the email address in this system, so the previous
   * implementation rendered the same value in both the Username and Email rows.
   * The name comes from users.firstname / users.lastname, which GET /profile
   * already returns.
   *
   * Either part may be missing or blank, so each is trimmed and only non-empty
   * parts are joined — that avoids "John undefined", a stray leading/trailing
   * space, or a lone space when both are empty. When there is no name at all we
   * keep the previous fallback chain rather than showing a dash, so accounts
   * created without a name still display something meaningful.
   */
  displayUsername(user: UserProfile): string {
    const fullName = [user.firstname, user.lastname]
      .map((part) => (part ?? "").trim())
      .filter((part) => part.length > 0)
      .join(" ")

    if (fullName) return fullName

    const u = user as UserProfile & { username?: string }
    return u.username || u.cognito_username || user.email || "—"
  }

  textNotificationsLabel(user: UserProfile): string {
    return textNotificationsEnabled(user.textnotifications) ? "Enabled" : "Disabled"
  }

  phoneVerified(user: UserProfile): boolean {
    const v = user.phone_verified
    if (v === true || v === 1) return true
    if (typeof v === "string") {
      const s = v.trim().toLowerCase()
      return s === "1" || s === "true" || s === "yes"
    }
    return false
  }

  showPhoneVerifiedCheckmark(user: UserProfile): boolean {
    return textNotificationsEnabled(user.textnotifications) && this.phoneVerified(user)
  }


  loadUserProfile(): void {
    this.loading = true

    const cached = this.profileService.getCurrentUser()
    if (cached) {
      this.user = { ...cached }
      console.log("User profile from cache:", this.user)
      this.loading = false
      return
    }

    const cachedProfile = localStorage.getItem("cachedUserProfile")
    let email: string | null = null

    if (cachedProfile) {
      try {
        const parsed = JSON.parse(cachedProfile)
        email = parsed.email
      } catch {
        console.error("Error parsing cached profile")
      }
    }

    if (!email) {
      this.loading = false
      console.warn("No email found. Redirecting to login.")
      this.router.navigate(["/login"])
      return
    }

    this.profileService.getProfile(email).subscribe({
      next: (data) => {
        this.user = { ...data }
        this.loading = false
      },
      error: (error) => {
        console.error("Profile fetch error:", error)
        this.error = error.error?.message || "Failed to load profile"
        this.loading = false

        if (error.status === 401) {
          // Interceptor clears session and redirects; avoid local token half-clears.
          return
        }
      },
    })
  }

  logout(): void {
    this.profileService.clearProfile()
    this.router.navigate(["/login"])
  }
}
